import React, { useCallback, useContext, useState, useEffect } from 'react';
import Card from '../../../components/card';
import Loader from '../../../components/loader';
import { useFetchDataAndSetState } from '../../../helpers/useFetchDataAndSetState';
import {
    getAllHours,
    getAvailableHours,
    getPrivateProjects,
    getProjects,
} from '../../../api/hours/HoursAPI';
import { UserContext } from '../../../context/UserContext';
import { OrganisationsContext } from '../../../context/OrganisationsContext';
import moment from 'moment';
import UserTimesheetSelect from '../../../components/timsheet-user-select';
import ErrorIndicator from '../../../components/error-indicator';
import { useTranslation } from 'react-i18next';
import { getAdminPermission } from '../../../helpers/getPermission';
import TimesheetVertical from '../timesheet-vertical';
import {
    PrivateProjectsContext,
    ProjectsContext,
} from '../../../context/ProjectsContext';

const WEEKDAY_MONDAY = 1;
const WEEKDAY_SUNDAY = 7;

function HoursPageTimesheet() {
    const { t } = useTranslation();

    const { projectsState, projectsDispatch } = useContext(ProjectsContext);
    const privateProjectsContext = useContext(PrivateProjectsContext);
    const { organisationsState } = useContext(OrganisationsContext);
    const selectedOrganisationHash = organisationsState.data
        ? organisationsState.data?.find((org) => org.active).org_hash
        : null;
    const demoOrgHash = process.env.REACT_APP_DEMO_ORG_HASH;

    const permissionForWrite = getAdminPermission(organisationsState?.data);

    const { userState } = useContext(UserContext);

    const [userHash, setUserHash] = useState(userState?.data?.user_hash);

    const [userProjects, setUserProjects] = useState(null);

    const [filterDate, setFilterDate] = useState(moment());

    const [availableHours, setAvailableHours] = useState(null);

    let weekRange = [];
    let weekDay = WEEKDAY_MONDAY;
    while (weekDay <= WEEKDAY_SUNDAY) {
        weekRange.push(filterDate.clone().day(weekDay));
        weekDay++;
    }

    const [updatesCounter, setUpdatesCounter] = useState(0);

    const request = useCallback(
        () =>
            getAllHours({
                params: {
                    filterDate: filterDate.clone().day(1).format('YYYY-MM-DD'),
                    interval: 6, // filterDate day + 6 more days
                },
            }),
        [filterDate]
    );
    const [hoursState] = useFetchDataAndSetState(
        request,
        [selectedOrganisationHash, updatesCounter],
        [{ selected_projects: [], projects_data: [] }]
    );
    const { data, loading, error } = hoursState;

    const [isSavingAllowed, setIsSavingAllowed] = useState(false);

    const [isEditMode, setIsEditMode] = useState(false);
    const [firstAddedProject, setFirstAddedPtroject] = useState(null);

    const [formattedData, setFormattedData] = useState(null);

    const [allProjectsWithNames, setAllProjectsWithNames] = useState(null);
    const [projectsToSelect, setProjectsToSelect] = useState(null);

    function insertProjectNames(projects) {
        const withNamesInserted = projects?.map((p) => {
            return {
                ...p,
                projectName: allProjectsWithNames?.find(
                    (proj) => proj.id === p.project
                )?.name,
            };
        });

        return withNamesInserted;
    }

    useEffect(() => {
        fetchProjects(filterDate.format('YYYY-MM-DD'));
    }, [filterDate]);

    useEffect(() => {
        const withNamesInserted = insertProjectNames(
            userProjects?.projects_data
        );
        setFormattedData(withNamesInserted);
    }, [allProjectsWithNames]);

    useEffect(() => {
        if (selectedOrganisationHash === demoOrgHash) {
            setUserHash('URICHAR');
        } else {
            setUserHash(userState?.data?.user_hash || null);
        }
    }, [selectedOrganisationHash]);

    useEffect(() => {
        setUserProjects(filterUserProjectsData(userHash));
    }, [userHash, data]);

    useEffect(() => {
        setUserHash(userState?.data?.user_hash);
    }, [userState?.data]);

    useEffect(() => {
        const weekStartMonth = weekRange[0]?.month() + 1;
        const weekEndMonth = weekRange[weekRange?.length - 1]?.month() + 1;
        const isSameMonth = weekStartMonth === weekEndMonth;
        let avalilableHoursList = null;
        if (isSameMonth) {
            avalilableHoursList = allProjectsWithNames?.reduce((acc, p) => {
                return {
                    ...acc,
                    [p.id]: {
                        [weekStartMonth]: p?.available_hours,
                        monthly: p?.hour_block?.monthly,
                    },
                };
            }, {});

            setAvailableHours(avalilableHoursList);
        } else {
            async function getMultipleMinthAvailableHours() {
                const promises = await allProjectsWithNames?.map(async (p) => {
                    const base = {
                        [weekStartMonth]: p?.available_hours,
                        [weekEndMonth]: p?.available_hours,
                        monthly: p?.hour_block?.monthly,
                    };

                    if (p?.hour_block && p?.hour_block?.monthly) {
                        const weekEndMonthAvailableHours =
                            await fetchAvailableHours(
                                p?.id,
                                weekEndMonth,
                                weekRange[weekRange?.length - 1]?.format('YYYY')
                            );

                        if (weekEndMonthAvailableHours === null) {
                            return { id: p.id, hours: base };
                        } else {
                            return {
                                id: p.id,
                                hours: {
                                    ...base,
                                    [weekEndMonth]: weekEndMonthAvailableHours,
                                },
                            };
                        }
                    }

                    return { id: p.id, hours: base };
                });

                const results = promises?.length ? await Promise.allSettled(promises) : [];
                const hoursList = results
                    .filter((r) => r.status === 'fulfilled')
                    .reduce((acc, r) => {
                        acc[r.value.id] = r.value.hours;
                        return acc;
                    }, {});

                setAvailableHours(hoursList);
                return;
            }

            getMultipleMinthAvailableHours();
        }
    }, [allProjectsWithNames]);

    useEffect(() => {
        compileProjectsList();
    }, [
        projectsState?.data,
        privateProjectsContext?.projectsState.data,
        userProjects?.selected_projects,
        userState?.data.user_hash,
        userHash,
    ]);

    async function fetchProjects(date) {
        try {
            const orgProjects = await getProjects({ params: { date } });

            if (orgProjects.status === 204) {
                projectsDispatch({ type: 'SET_DATA', payload: [] });
            } else {
                projectsDispatch({
                    type: 'SET_DATA',
                    payload: orgProjects.data,
                });
            }
        } catch (err) {
            projectsDispatch({ type: 'SET_ERROR', payload: err });
        }
        try {
            const userProjects = await getPrivateProjects(userHash, {
                params: { date },
            });
            if (userProjects.status === 204) {
                privateProjectsContext.projectsDispatch({
                    type: 'SET_DATA',
                    payload: [],
                });
            } else {
                privateProjectsContext.projectsDispatch({
                    type: 'SET_DATA',
                    payload: userProjects.data,
                });
            }
        } catch (err) {
            privateProjectsContext.projectsDispatch({
                type: 'SET_ERROR',
                payload: err,
            });
        }
    }

    async function fetchAvailableHours(project, month, year) {
        const monthDate = `${year}-${month > 9 ? month : `0${month}`}-01`;
        try {
            const res = await getAvailableHours({
                params: { project, date: monthDate },
            });

            if (res?.data) {
                return res?.data;
            }
            return null;
        } catch (err) {
            return null;
        }
    }

    function compileProjectsList() {
        let allProjects = projectsState?.data?.length
            ? [...projectsState.data]
            : [];

        if (userHash === userState?.data.user_hash) {
            allProjects = privateProjectsContext?.projectsState.data?.length
                ? [
                      ...allProjects,
                      ...privateProjectsContext?.projectsState.data,
                  ]
                : [...allProjects];
        }

        setAllProjectsWithNames(allProjects);

        const notSelectedProjects = allProjects.filter((project) => {
            const isSelected = userProjects?.selected_projects?.find(
                (selectedProject) => selectedProject === project.name
            );
            if (!isSelected) {
                return project;
            }
        });

        const projectOptions = notSelectedProjects
            .sort((a, b) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            )
            .map(({ name, id }) => ({ value: id, label: name }));

        setProjectsToSelect(projectOptions);
    }

    function handleUpdateTable() {
        setUpdatesCounter(updatesCounter + 1);
    }

    function renderUserSelect() {
        return (
            <UserTimesheetSelect
                isPermission={permissionForWrite}
                permission={
                    organisationsState.data
                        ? organisationsState.data?.find((org) => org.active)
                              .permissions
                        : null
                }
                setUserHash={setUserHash}
                viewerData={userState?.data}
                userHash={userHash}
                isDisabled={isEditMode}
            />
        );
    }

    function filterUserProjectsData(userHash) {
        const user = data
            ? data?.find((userData) => userData?.user?.hash === userHash)
            : null;
        if (user) {
            return user;
        } else {
            return { projects_data: [], selected_projects: [] };
        }
    }

    function handleReset() {
        const withNamesInserted = insertProjectNames(
            userProjects?.projects_data
        );
        setFormattedData(withNamesInserted);
        setIsEditMode(false);
        setFirstAddedPtroject(null);
        compileProjectsList();
        setIsSavingAllowed(false);
    }

    return (
        <Card
            title={t('hours_page.timesheet.title')}
            width={'w-full '}
            renderActionButtons={renderUserSelect}
        >
            {userHash ? (
                <div>
                    {loading && (
                        <div className="flex mt-8 justify-center">
                            <Loader
                                color={'#C2C7D7'}
                                size={35}
                                speedMultiplier={0.8}
                            />
                        </div>
                    )}

                    {error && !loading && <ErrorIndicator error={error} />}
                    {!loading && !error && (
                        <TimesheetVertical
                            data={formattedData}
                            weekRange={weekRange}
                            isEditMode={isEditMode}
                            setIsEditMode={setIsEditMode}
                            firstAddedProject={firstAddedProject}
                            setFirstAddedPtroject={setFirstAddedPtroject}
                            projectsToSelect={projectsToSelect}
                            setProjectsToSelect={setProjectsToSelect}
                            isSavingAllowed={isSavingAllowed}
                            setIsSavingAllowed={setIsSavingAllowed}
                            filterDate={filterDate}
                            setFilterDate={setFilterDate}
                            handleUpdateTable={handleUpdateTable}
                            handleReset={handleReset}
                            triggerUpdate={handleUpdateTable}
                            userHash={userHash}
                            globalUserHash={userState?.data?.user_hash}
                            availableHours={availableHours}
                        />
                    )}
                </div>
            ) : (
                <p className="font-display text-center text-gray-500 text-xl p-4">
                    {' '}
                    No users in the organisation
                </p>
            )}
        </Card>
    );
}

export default HoursPageTimesheet;
